<template>
          <template v-if="$route.name === 'settings.reward-points.index'">

  <div class="col-12 col-xl-12">
       <!-- Filters -->
        <div class="container-fluid px-0">
          <div class="row mb-4 g-2 p-3 p-xl-4">
            <!-- Type Filter -->
            <div class="col-4 col-md-3 col-lg-3">
              <select
                class="form-select form-select-sm"
                v-model="localFilters.type"
                @change="applyFilters"
              >
                <option value="">{{ $t('reward_points.filters.allTypes') }}</option>
                <option value="positive">{{ $t('reward_points.filters.positive') }}</option>
                <option value="negative">{{ $t('reward_points.filters.negative') }}</option>
              </select>
            </div>

    
            <!-- Reset Filter (desktop) -->
            <div class="col-md-1 d-none d-md-block">
              <button 
                class="btn btn-outline-secondary btn-sm w-100" 
                @click="resetFilters"
                :title="$t('filters.resetTitle')"
              >
                <i class="fas fa-filter-circle-xmark"></i>
              </button>
            </div>

            <!-- Search -->
            <div class="col-4 col-sm-4 col-md-4 col-lg-4 position-relative">
              <input
                type="text"
                class="form-control form-control-sm ps-5"
                :placeholder="$t('reward_points.filters.searchPlaceholder')"
                v-model="localFilters.search"
                @input="handleSearchInput"
                style="padding-right: 12px"
              />
              <span class="position-absolute start-0 top-50 translate-middle-y ps-3">
                <i class="fas fa-search"></i>
              </span>
            </div>

            <!-- Reset Filter (mobile) -->
            <div class="col-4 d-md-none">
              <button class="btn btn-outline-secondary btn-sm w-100" @click="resetFilters">
                <i class="fas fa-filter-circle-xmark"></i> 
              </button>
            </div>

            <!-- Add Reward Point -->
            <div class=" col-md-4 col-lg-4">
              <router-link
                :to="{ name: 'settings.reward-points.add' }"
                class="btn btn-secondary btn-sm w-100"
                v-can="'reward_points.create'"
              >
                <i class="fas fa-plus"></i>
                {{ $t('reward_points.addNew') }}
              </router-link>
            </div>
          </div>
        </div>
    <div class="card mb-3 mb-xxl-5 me-xxl-4" style="margin-top: 4rem; overflow: visible">
      <!-- Card Header -->
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <h6 class="text-white text-capitalize ps-3 pe-3" style="font-size: 1rem">
            {{ $t('reward_points.tableTitle') }}
          </h6>
        </div>
      </div>

      <div class="card-body px-0 pb-2 pe-xl-3">
     

        <!-- Reward Points Table -->
        <div class="table-responsive p-1 p-xl-4">
          <LoadingSpinner
            v-if="loading"
            :message="$t('common.loading')"
            class="py-5"
          />

          <template v-else>
            <!-- Mobile View -->
            <div class="d-block d-xl-none">
              <div v-if="rewardPoints.length === 0" class="text-center py-3 text-muted">
                <i class="fas fa-star fa-2x mb-2"></i>
                <p>{{ $t('reward_points.empty') }}</p>
              </div>

              <div v-for="point in rewardPoints" :key="'mobile-' + point.id" class="card mb-3 mx-2">
                <div class="card-body py-2">
                  <div class="d-flex justify-content-between align-items-start">
                    <div class="d-flex align-items-center">
                      <div class="avatar avatar-sm me-3">
                        <i :class="`fas ${point.icon} fa-lg text-${point.type === 'positive' ? 'success' : 'danger'}`"></i>
                      </div>
                      <div>
                        <h6 class="mb-0 text-sm">{{ point.name }}</h6>
                        <p class="text-xs text-muted mb-0">
                          {{ point.description || $t('reward_points.noDescription') }}
                        </p>
                      </div>
                    </div>
                    <div class="d-flex flex-column align-items-end">
                      <span class="badge mb-1" :class="point.type === 'positive' ? 'bg-success' : 'bg-danger'">
                        {{ point.type === 'positive' ? '+' : '-' }}{{ point.points }}
                      </span>
                      <span class="badge" :class="point.is_active ? 'bg-success' : 'bg-danger'">
                        {{ point.is_active ? $t('reward_points.active') : $t('reward_points.inactive') }}
                      </span>
                    </div>
                  </div>

                  <div class="d-flex justify-content-center mt-2">
                    <button
                      @click="navigateToEdit(point.id)"
                      class="btn btn-link text-secondary p-0 mx-1"
                      :title="$t('common.edit')"
                      v-can="'reward_points.update'"
                    >
                      <i class="fas fa-edit fa-xs"></i>
                    </button>
                    <button
                      @click="openDeleteDialog(point)"
                      class="btn btn-link text-danger p-0 mx-1"
                      :title="$t('common.delete')"
                      v-can="'reward_points.delete'"
                    >
                      <i class="fas fa-trash fa-xs"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Desktop View -->
            <table class="table align-items-center mb-0 d-none d-xl-table">
              <thead>
                <tr>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('reward_points.icon') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('reward_points.name') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('reward_points.type') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('reward_points.points') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('reward_points.status') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center pe-4">{{ $t('common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="rewardPoints.length === 0">
                  <td colspan="6" class="text-center py-4 text-muted">
                    <i class="fas fa-star fa-2x mb-2"></i>
                    <p>{{ $t('reward_points.empty') }}</p>
                  </td>
                </tr>

                <tr v-for="point in rewardPoints" :key="point.id">
                  <td>
                    <div class="avatar avatar-sm">
                      <i :class="`fas ${point.icon} fa-lg text-${point.type === 'positive' ? 'success' : 'danger'}`"></i>
                    </div>
                  </td>
                  <td>
                    <div class="d-flex flex-column">
                      <h6 class="mb-0 text-sm">{{ point.name }}</h6>
                      <p class="text-xs text-secondary mb-0">
                        {{ point.description || $t('reward_points.noDescription') }}
                      </p>
                    </div>
                  </td>
                  <td>
                    <span class="badge" :class="point.type === 'positive' ? 'bg-success' : 'bg-danger'">
                      {{ point.type === 'positive' ? $t('reward_points.positive') : $t('reward_points.negative') }}
                    </span>
                  </td>
                  <td class="align-middle text-center">
                    <span class="badge" :class="point.type === 'positive' ? 'bg-success' : 'bg-danger'">
                      {{ point.type === 'positive' ? '+' : '-' }}{{ point.points }}
                    </span>
                  </td>
                  <td class="align-middle text-center">
                    <span class="badge" :class="point.is_active ? 'bg-success' : 'bg-danger'">
                      {{ point.is_active ? $t('reward_points.active') : $t('reward_points.inactive') }}
                    </span>
                  </td>
                  <td class="align-middle text-center pe-4">
                    <div class="d-inline-flex">
                      <button
                        @click="navigateToEdit(point.id)"
                        class="btn btn-link text-secondary p-1 mx-1"
                        :title="$t('common.edit')"
                        v-can="'reward_points.update'"
                      >
                        <i class="fas fa-edit fa-sm"></i>
                      </button>
                      <button
                        @click="openDeleteDialog(point)"
                        class="btn btn-link text-danger p-1 mx-1"
                        :title="$t('common.delete')"
                        v-can="'reward_points.delete'"
                      >
                        <i class="fas fa-trash fa-sm"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </template>
        </div>
      </div>
    </div>

    <!-- Delete Dialog -->
    <DeleteRewardPointDialog
      ref="deleteDialog"
      @delete="handleDeletePoint"
    />
  </div>
  </template>
    <router-view></router-view>
</template>

<script setup>
import { ref, onMounted, watch, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from 'vue-toastification'
import api from '@/api'
import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
import DeleteRewardPointDialog from '@/components/features/student-rewards/settings/DeleteRewardPointDialog.vue'

const router = useRouter()
const { t } = useI18n()
const toast = useToast()

const loading = ref(false)
const rewardPoints = ref([])
const deleteDialog = ref(null)
const selectedPoint = ref(null)

let debounceTimeout = null
const DEBOUNCE_DELAY = 500 // 500ms delay

const filters = ref({
  type: '',
  is_active: '',
  search: ''
})

const localFilters = ref({
  ...JSON.parse(JSON.stringify(filters.value))
})

onUnmounted(() => {
  if (debounceTimeout) {
    clearTimeout(debounceTimeout)
  }
})

// Handle search input with debounce
const handleSearchInput = () => {
  // Clear existing timeout
  if (debounceTimeout) {
    clearTimeout(debounceTimeout)
  }
  
  // Set new timeout
  debounceTimeout = setTimeout(() => {
    applyFilters()
    debounceTimeout = null
  }, DEBOUNCE_DELAY)
}

const applyFilters = () => {
  // Update filters with local values (convert empty strings to null)
  filters.value = {
    type: localFilters.value.type || null,
    is_active: localFilters.value.is_active || null,
    search: localFilters.value.search || null
  }
  
  fetchRewardPoints()
}

const resetFilters = () => {
  // Clear any pending debounce timeout
  if (debounceTimeout) {
    clearTimeout(debounceTimeout)
    debounceTimeout = null
  }
  
  // Reset local filters
  localFilters.value = {
    type: '',
    is_active: '',
    search: ''
  }
  
  // Apply reset immediately
  applyFilters()
}

const fetchRewardPoints = async () => {
  loading.value = true
  try {
    // Clean up params - remove null values
    const params = {}
    if (filters.value.type) params.type = filters.value.type
    if (filters.value.is_active !== null && filters.value.is_active !== '') {
      params.is_active = filters.value.is_active
    }
    if (filters.value.search) params.search = filters.value.search
    
    console.log('Fetching reward points with params:', params) // Debug log
    
    const response = await api.get('/reward-points', { params })
    rewardPoints.value = response.data.data
    
    // Debug log to see what's returned
    console.log('Received reward points:', rewardPoints.value)
    
  } catch (error) {
    console.error('Error fetching reward points:', error)
    toast.error(t('errors.fetch_failed'))
  } finally {
    loading.value = false
  }
}

const navigateToEdit = (pointId) => {
  router.push(`/settings/reward-points/${pointId}/edit`)
}

const openDeleteDialog = (point) => {
  selectedPoint.value = point
  deleteDialog.value.openDialog(point.name)
}

const handleDeletePoint = async () => {
  try {
    await api.delete(`/reward-points/${selectedPoint.value.id}`)

    toast.success(
      t('reward_points.messages.deleted')
    )

    deleteDialog.value?.closeDialog()
    selectedPoint.value = null
    fetchRewardPoints()
  } catch (error) {
    console.error('Error deleting reward point:', error)

    deleteDialog.value?.closeDialog()
    selectedPoint.value = null

    toast.error(
      error.response?.data?.message || t('errors.delete_failed')
    )
  }
}
onMounted(() => {
  fetchRewardPoints()
})

// Watch for filter changes and apply them
watch(filters, () => {
  fetchRewardPoints()
}, { deep: true })

// Debug log for local filters
watch(() => localFilters.value, (newVal) => {
  console.log('Local filters changed:', newVal)
}, { deep: true })
</script>

